import { InjectionToken } from '@angular/core';
import type {
  Appearance,
  CustomFontSource,
  Stripe,
  StripeElementLocale,
  StripeElements,
  StripeElementsOptionsMode,
  StripePaymentElement,
} from '@stripe/stripe-js';

import { ActivationCurrency } from './activation-checkout';

/** Loads Stripe.js for a publishable key. A token so tests can hand in a fake Stripe. */
export type StripeLoader = (publishableKey: string) => Promise<Stripe | null>;

export const STRIPE_LOADER = new InjectionToken<StripeLoader>('STRIPE_LOADER', {
  providedIn: 'root',
  // The pure entry does not inject the Stripe script on import, and the dynamic import keeps
  // Stripe.js out of the main bundle: it is only fetched when someone opens the payment step.
  factory: () => async (publishableKey: string) => {
    const { loadStripe } = await import('@stripe/stripe-js/pure');
    return loadStripe(publishableKey);
  },
});

/*
 * The Payment Element renders in a Stripe iframe, which cannot read our CSS
 * tokens or Tailwind classes. These values mirror the design system:
 * Hype Marine (primary), black text, error red, rounded-md radius, Spartan.
 */
const HYPE_MARINE = '#0029FF';
const TEXT_BLACK = '#090909';
const ERROR_RED = '#9B0E0E';
const FIELD_BACKGROUND = '#F6FEFF';
const FONT_FAMILY = 'Spartan';

export function activationAppearance(): Appearance {
  return {
    theme: 'stripe',
    variables: {
      colorPrimary: HYPE_MARINE,
      colorText: TEXT_BLACK,
      colorDanger: ERROR_RED,
      colorBackground: FIELD_BACKGROUND,
      fontFamily: `${FONT_FAMILY}, system-ui, sans-serif`,
      borderRadius: '6px',
      spacingUnit: '4px',
    },
    rules: {
      '.Label': { fontWeight: '600' },
      '.Input:focus': { borderColor: HYPE_MARINE },
    },
  };
}

/** Spartan for the Stripe iframe; nginx serves /assets/fonts/ with CORS for it. */
export function activationFonts(origin: string): CustomFontSource[] {
  return [
    {
      family: FONT_FAMILY,
      src: `url(${origin}/assets/fonts/Spartan-VariableFont_wght.woff2)`,
      weight: '100 900',
      display: 'swap',
    },
  ];
}

/** Stripe's UI language from the app locale ("de-DE" → "de"). */
export function stripeLocale(locale: string): StripeElementLocale {
  return (locale.split('-')[0]?.toLowerCase() || 'auto') as StripeElementLocale;
}

export type ActivationPaymentMethods = Pick<
  StripeElementsOptionsMode,
  'allowedPaymentMethodTypes' | 'paymentMethodOptions'
>;

/** Card first, then the bank debit of the currency. */
export const ACTIVATION_PAYMENT_METHOD_ORDER = ['card', 'sepa_debit', 'us_bank_account'];

/**
 * The payment methods the Payment Element offers, the same as the master's SetupIntent
 * (ActivationPaymentMethods): card plus SEPA Direct Debit, for USD card plus ACH with instant
 * verification (microdeposits would leave the SetupIntent in requires_action, the master only
 * books a succeeded one). Without the list the element shows every method enabled in the
 * dashboard, and confirmSetup fails for those the SetupIntent does not allow.
 */
export function activationPaymentMethods(currency: ActivationCurrency): ActivationPaymentMethods {
  return currency === 'usd'
    ? {
        allowedPaymentMethodTypes: ['card', 'us_bank_account'],
        paymentMethodOptions: { us_bank_account: { verification_method: 'instant' } },
      }
    : { allowedPaymentMethodTypes: ['card', 'sepa_debit'] };
}

export type StripeConfirmOutcome = { setupIntentId: string } | { error: string };

export interface StripeSetupFormOptions {
  publishableKey: string;
  currency: ActivationCurrency;
  locale: string;
  origin: string;
}

/**
 * Payment Element in deferred-intent mode: the element is created with mode
 * `setup` and the currency, the SetupIntent is only created on submit
 * (elements.submit → POST setup-intent → stripe.confirmSetup).
 */
export class StripeSetupForm {
  private ready = false;
  private readonly readyListeners: ((ready: boolean) => void)[] = [];
  private loadErrorListener: ((message: string) => void) | null = null;

  private constructor(
    private readonly stripe: Stripe,
    private readonly elements: StripeElements,
    private readonly element: StripePaymentElement,
  ) {
    element.on('ready', () => this.setReady(true));
    element.on('loaderror', (event) => this.loadErrorListener?.(event.error.message ?? ''));
  }

  /** Null when Stripe.js could not be loaded (blocked script, offline). */
  static async create(loader: StripeLoader, options: StripeSetupFormOptions): Promise<StripeSetupForm | null> {
    const stripe = await loader(options.publishableKey);
    if (stripe === null) {
      return null;
    }
    const elements = stripe.elements({
      mode: 'setup',
      currency: options.currency,
      ...activationPaymentMethods(options.currency),
      appearance: activationAppearance(),
      fonts: activationFonts(options.origin),
      locale: stripeLocale(options.locale),
    });
    const element = elements.create('payment', {
      layout: 'tabs',
      paymentMethodOrder: ACTIVATION_PAYMENT_METHOD_ORDER,
    });
    return new StripeSetupForm(stripe, elements, element);
  }

  onReadyChange(listener: (ready: boolean) => void): void {
    this.readyListeners.push(listener);
  }

  onLoadError(listener: (message: string) => void): void {
    this.loadErrorListener = listener;
  }

  mount(host: HTMLElement): void {
    this.setReady(false);
    this.element.mount(host);
  }

  unmount(): void {
    this.setReady(false);
    this.element.unmount();
  }

  destroy(): void {
    this.readyListeners.length = 0;
    this.element.destroy();
  }

  /** Validates the form (and collects wallet data). Returns Stripe's message when something is missing. */
  async submit(): Promise<string | null> {
    const result = await this.elements.submit();
    return result.error ? (result.error.message ?? '') : null;
  }

  /**
   * Confirms the SetupIntent. Card payments finish here; a payment method that
   * needs a redirect leaves to Stripe and comes back to `returnUrl`.
   */
  async confirm(clientSecret: string, returnUrl: string): Promise<StripeConfirmOutcome> {
    const result = await this.stripe.confirmSetup({
      elements: this.elements,
      clientSecret,
      redirect: 'if_required',
      confirmParams: { return_url: returnUrl },
    });
    if (result.error) {
      return { error: result.error.message ?? '' };
    }
    return { setupIntentId: result.setupIntent.id };
  }

  private setReady(ready: boolean): void {
    if (this.ready === ready) {
      return;
    }
    this.ready = ready;
    this.readyListeners.forEach((listener) => listener(ready));
  }
}
